import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface AudioPostResponse {
  id: string;
  url: string;
}

export interface UiAudioPostResponse extends AudioPostResponse {
  audioIdType?: 'greetingAudioId' | 'musicOnHoldAudioId' | 'offerAudioPromptFileGuid';
}

export type AudioType = 'call-queues' | 'auto-attendant' | 'ring-central';

const AUDIO_MIME_TYPES_BY_EXTENSION: Record<string, string> = {
  '.wav': 'audio/wav',
  '.mp3': 'audio/mpeg',
  '.wma': 'audio/x-ms-wma',
};

@Injectable({
  providedIn: 'root',
})
export class AudioFileResource {
  private _http = inject(HttpClient);

  private readonly _audioTypeUrlMap = new Map<AudioType, (tenantId: string, exportId?: string) => string>([
    [
      'auto-attendant',
      (tenantId) => `/services/microsoft/auto-attendant-management/tenants/${tenantId}/audio-files/auto-attendant`,
    ],
    [
      'call-queues',
      (tenantId) => `/services/microsoft/call-queue-management/tenants/${tenantId}/audio-files/call-queues`,
    ],
    [
      'ring-central',
      (tenantId, exportId) =>
        `/services/microsoft/migrations/tenants/${tenantId}/exports/ring-central/${exportId}/audio`,
    ],
  ]);

  getAudioFileUrl(tenantId: string, audioType: AudioType, filename: string, exportId?: string): string {
    return `${this._audioTypeUrlMap.get(audioType)(tenantId, exportId)}/${filename}`;
  }

  getAudioFile(tenantId: string, audioType: AudioType, filename: string, exportId?: string): Observable<File> {
    const url = this.getAudioFileUrl(tenantId, audioType, filename, exportId);
    return this._http
      .get(url, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((response) => {
          const downloadFilename = response.headers.get('content-disposition').split('attachment; filename=')[1];
          const extension = downloadFilename.substring(downloadFilename.lastIndexOf('.')).toLowerCase();
          return new File([response.body], downloadFilename, { type: AUDIO_MIME_TYPES_BY_EXTENSION[extension] });
        }),
      );
  }

  createAudioFile(tenantId: string, audioType: AudioType, file: File): Observable<AudioPostResponse> {
    const baseUrl = this._audioTypeUrlMap.get(audioType)(tenantId);
    const formData = new FormData();
    formData.append('upload', file, file.name);
    return this._http.post<AudioPostResponse>(baseUrl, formData);
  }
}
